import { Component, effect, inject, input, output, signal } from '@angular/core';
import { Dialog } from 'primeng/dialog';
import { Chip } from 'primeng/chip';
import { firstValueFrom } from 'rxjs';

import {
  AppointmentDetailsForAdminResponse,
  AppointmentsClient,
  AppointmentStatus,
  DocumentDto,
  FileType,
} from '../../../infrastructure/shared/model/fss.api';

import {
  ButtonComponent,
  ButtonSeverity,
  ButtonSize,
} from '../../../infrastructure/shared/ui/button/button';
import { XClose } from '../../../infrastructure/shared/icons/x-close/x-close';
import { SlashCircle01 } from '../../../infrastructure/shared/icons/slash-circle-01/slash-circle-01';
import { Calendar } from '../../../infrastructure/shared/icons/calendar/calendar';
import { Clock } from '../../../infrastructure/shared/icons/clock/clock';
import { UserCircle } from '../../../infrastructure/shared/icons/user-circle/user-circle';
import { Building05 } from '../../../infrastructure/shared/icons/building-05/building-05';
import { Arrow } from '../../../infrastructure/shared/icons/arrow/arrow';
import { AlertTriangle } from '../../../infrastructure/shared/icons/alert-triangle/alert-triangle';
import { Hourglas03 } from '../../../infrastructure/shared/icons/hourglas-03/hourglas-03';
import { Pdf } from '../../../infrastructure/shared/icons/pdf/pdf';
import { Dwg } from '../../../infrastructure/shared/icons/dwg/dwg';
import { Download01 } from '../../../infrastructure/shared/icons/download-01/download-01';
import { Edit03 } from '../../../infrastructure/shared/icons/edit-03/edit-03';
import {
  getDateFormatedSrLong,
  getDateFormatedSrWithTime,
} from '../../../infrastructure/shared/services/date.service';
import { LoaderService } from '../../../infrastructure/shared/components/loader/loader.service';
import { AlertService } from '../../../infrastructure/shared/services/alert.service';
import { LocationAdminService } from '../../../infrastructure/shared/services/location-admin.service';
import { FileService } from '../../../infrastructure/shared/services/file.service';
import { AppointmentRescheduleDialog } from '../admin-appointments-table/appointment-reschedule-dialog/appointment-reschedule-dialog';

type DetailsState = 'scheduled' | 'completed' | 'cancelled' | 'noShow';

@Component({
  standalone: true,
  selector: 'app-appointment-details-content',
  imports: [
    Chip,
    ButtonComponent,
    XClose,
    SlashCircle01,
    Calendar,
    Clock,
    UserCircle,
    Building05,
    Arrow,
    Hourglas03,
    Pdf,
    Dwg,
    Download01,
    Edit03,
    Dialog,
    AlertTriangle,
    AppointmentRescheduleDialog,
  ],
  templateUrl: './appointment-details-content.html',
  styleUrl: './appointment-details-content.scss',
})
export class AppointmentDetailsContent {
  private _client = inject(AppointmentsClient);
  private _loader = inject(LoaderService);
  private _alert = inject(AlertService);
  private _location = inject(LocationAdminService);
  private _file = inject(FileService);

  appointmentId = input<string | undefined>(undefined);
  /** When true, hides the action footer (Otkaži/Izmeni/Označi kao nedolazak) */
  hideActions = input<boolean>(false);

  onCancel = output<string>();
  onRescheduled = output<string>();
  onMarkedNoShow = output<string>();

  details = signal<AppointmentDetailsForAdminResponse | undefined>(undefined);
  isLoading = signal(false);
  showCancelDialog = signal(false);
  showRescheduleDialog = signal(false);
  showNoShowDialog = signal(false);

  constructor() {
    effect(() => {
      const id = this.appointmentId();
      if (id) {
        this._load(id);
      } else {
        this.details.set(undefined);
      }
    });
  }

  state(): DetailsState {
    const d = this.details();
    if (!d) return 'scheduled';
    if (d.status?.value === AppointmentStatus.CANCELED) return 'cancelled';
    if (d.status?.value === AppointmentStatus.NO_SHOW) return 'noShow';
    if (d.to && d.to.getTime() < Date.now()) return 'completed';
    return 'scheduled';
  }

  statusChipClass(): string {
    const s = this.state();
    if (s === 'cancelled') return 'red sm';
    if (s === 'noShow') return 'warn sm';
    if (s === 'completed') return 'gray sm';
    return 'info sm';
  }

  canMarkNoShow(): boolean {
    const d = this.details();
    if (!d) return false;
    return d.status?.value === AppointmentStatus.ACTIVE
      && !!d.to
      && d.to.getTime() < Date.now();
  }

  handleMarkNoShow() {
    if (this.details()?.id) this.showNoShowDialog.set(true);
  }

  closeNoShowDialog() {
    this.showNoShowDialog.set(false);
  }

  confirmMarkNoShow() {
    const id = this.details()?.id;
    if (!id) return;
    this._loader.setLoadingText = 'Označavanje...';
    this._loader.setShowLoader = true;
    firstValueFrom(this._client.markNoShow(id)).then((res) => {
      this.showNoShowDialog.set(false);
      this._alert.addSuccessMsg(res.result);
      this.onMarkedNoShow.emit(id);
      this._load(id);
    });
  }

  formatTimeRange(from?: Date, to?: Date): string {
    if (!from || !to) return '';
    const opts: Intl.DateTimeFormatOptions = { hour: '2-digit', minute: '2-digit', hour12: false };
    return `${from.toLocaleTimeString('en-GB', opts)} - ${to.toLocaleTimeString('en-GB', opts)}`;
  }

  formatFileSize(size?: number): string {
    if (!size) return '0KB';
    if (size < 1024) return `${size}B`;
    if (size < 1024 * 1024) return `${Math.round(size / 1024)}KB`;
    return `${(size / (1024 * 1024)).toFixed(1)}MB`;
  }

  trackDoc(_: number, doc: DocumentDto) {
    return doc.id;
  }

  download(doc: DocumentDto) {
    if (!doc.downloadUrl) return;
    this._loader.setLoadingText = 'Preuzimanje...';
    this._loader.setShowLoader = true;
    this._file.downloadFileFromPresignedUrl(doc.downloadUrl, doc.name ?? 'fajl');
  }

  handleCancel() {
    if (this.details()?.id) this.showCancelDialog.set(true);
  }

  closeCancelDialog() {
    this.showCancelDialog.set(false);
  }

  confirmCancel() {
    const id = this.details()?.id;
    if (!id) return;
    this._loader.setLoadingText = 'Otkazivanje...';
    this._loader.setShowLoader = true;
    firstValueFrom(this._client.cancelAppointment(id)).then((res) => {
      this.showCancelDialog.set(false);
      this._alert.addSuccessMsg(res.result);
      this.onCancel.emit(id);
      this._load(id);
    });
  }

  handleEdit() {
    if (this.details()?.id) this.showRescheduleDialog.set(true);
  }

  onRescheduleSuccess(id: string) {
    this.onRescheduled.emit(id);
    this._load(id);
  }

  handleViewClient() {
    const clientId = this.details()?.client?.id;
    if (!clientId) return;
    this._location.routeToClientDetails(clientId);
  }

  private async _load(id: string) {
    this.isLoading.set(true);
    try {
      const res = await firstValueFrom(this._client.getAppointmentDetailsForAdmin(id));
      this.details.set(res.result);
    } finally {
      this.isLoading.set(false);
    }
  }

  protected readonly ButtonSize = ButtonSize;
  protected readonly ButtonSeverity = ButtonSeverity;
  protected readonly getDateFormatedSrWithTime = getDateFormatedSrWithTime;
  protected readonly getDateFormatedSrLong = getDateFormatedSrLong;
  protected readonly FileType = FileType;
}
